import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';
import {
  Building2,
  XCircle,
  Euro,
  ArrowLeft,
  FileText,
  Users,
  CheckCircle2,
  Clock,
  Download,
  ShieldCheck,
  AlertTriangle,
  AlertCircle,
} from 'lucide-react';

// ─── types ────────────────────────────────────────────────────────────────────

interface ProfessionalSummary {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  hourly_rate?: number;
  professional_type?: string;
  kvk_nummer?: string;
  aantal_opdrachtgevers: number;
  aantal_geweigerd: number;
  eigen_tarieven: number[];
}

interface ConnectionRecord {
  connection_id: number;
  employer_id: string;
  employer_first_name: string;
  employer_last_name: string;
  employer_email: string;
  bedrijfsnaam?: string;
  status?: string;
  connected_at?: string;
  terminated_at?: string;
  connection_type?: string;
  termination_reason?: string;
  aangevraagd_op?: string;
}

interface AfspraakRecord {
  afspraak_id: number;
  connection_id: number;
  professional_function: string;
  hourly_rate?: number;
  max_conversations_per_traject?: number;
  conversation_report_frequency?: string;
  additional_agreements?: string;
  approved_by_professional: boolean;
  approved_by_employer: boolean;
  approved_at?: string;
  created_at: string;
  employer_first_name: string;
  employer_last_name: string;
  employer_email: string;
  bedrijfsnaam?: string;
}

interface ProfessionalDetail {
  professional: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    hourly_rate?: number;
    professional_type?: string;
    kvk_nummer?: string;
    bedrijfsnaam?: string;
    kvk_inschrijvingsjaar?: number;
    created_at: string;
  };
  opdrachtgevers: ConnectionRecord[];
  geweigerd: ConnectionRecord[];
  afspraken: AfspraakRecord[];
}

interface EmployerSummary {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  bedrijfsnaam?: string;
  aantal_professionals: number;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatDate(dateString?: string) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function formatEuro(amount?: number) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(amount);
}

function employerName(record: { bedrijfsnaam?: string; employer_first_name: string; employer_last_name: string }) {
  return record.bedrijfsnaam || `${record.employer_first_name} ${record.employer_last_name}`;
}

function complianceScore(prof: ProfessionalSummary): number {
  return [
    !!prof.kvk_nummer,
    prof.aantal_geweigerd > 0,
    prof.aantal_opdrachtgevers >= 2,
    prof.eigen_tarieven.length > 0,
  ].filter(Boolean).length;
}

function ScoreBadge({ score }: { score: number }) {
  if (score === 4 || score === 5) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
        <ShieldCheck className="h-3 w-3" />{score}/5
      </span>
    );
  }
  if (score >= 2) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800">
        <AlertTriangle className="h-3 w-3" />{score}/5
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">
      <AlertCircle className="h-3 w-3" />{score}/5
    </span>
  );
}

// ─── PDF download helper ──────────────────────────────────────────────────────

async function downloadPdf(url: string, filename: string) {
  const resp = await api.get(url, { responseType: 'blob' });
  const blob = new Blob([resp.data], { type: 'application/pdf' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

// ─── Detail view (per professional) ──────────────────────────────────────────

const ProfessionalDetailView: React.FC<{ professionalId: string; onBack: () => void }> = ({
  professionalId,
  onBack,
}) => {
  const { t } = useTranslation('pages');
  const [downloading, setDownloading] = useState(false);

  const { data, isLoading, isError } = useQuery<ProfessionalDetail>({
    queryKey: ['belastingdienst-detail', professionalId],
    queryFn: async () => {
      const resp = await api.get(`/admin/belastingdienst/professional/${professionalId}`);
      return resp.data.data;
    },
  });

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const name = data
        ? `compliance-${data.professional.last_name.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`
        : `compliance-${professionalId}.pdf`;
      await downloadPdf(`/admin/compliance/professional/${professionalId}/pdf`, name);
    } finally {
      setDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-center py-20 text-red-600">
        {t('belastingdienst.loadDataError')}
        <button onClick={onBack} className="ml-4 underline text-blue-600">{t('belastingdienst.backLink')}</button>
      </div>
    );
  }

  const { professional, opdrachtgevers, geweigerd, afspraken } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('belastingdienst.detailBack')}
        </button>
        <button
          onClick={handleDownloadPdf}
          disabled={downloading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 transition-colors"
        >
          <Download className="h-4 w-4" />
          {downloading ? t('belastingdienst.generating') : t('belastingdienst.downloadCompliancePdf')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900">
          {professional.first_name} {professional.last_name}
        </h2>
        <div className="mt-1 flex flex-wrap gap-4 text-sm text-gray-600">
          <span>{professional.email}</span>
          {professional.kvk_nummer && <span>{t('belastingdienst.kvkLabel')} {professional.kvk_nummer}</span>}
          {professional.bedrijfsnaam && <span>{t('belastingdienst.companyLabel')} {professional.bedrijfsnaam}</span>}
          {professional.professional_type && <span>{t('belastingdienst.typeLabel')} {professional.professional_type.replace(/_/g, ' ')}</span>}
          <span>{t('belastingdienst.memberSince')} {formatDate(professional.created_at)}</span>
        </div>
        {professional.hourly_rate && (
          <div className="mt-2 inline-flex items-center gap-1 bg-green-50 text-green-800 text-sm font-medium px-3 py-1 rounded-full">
            <Euro className="h-3.5 w-3.5" />
            {t('belastingdienst.baseRate')} {formatEuro(professional.hourly_rate)}{t('belastingdienst.perHour')}
          </div>
        )}
      </div>

      {/* Summary badges */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-blue-50 rounded-xl p-4 flex items-center gap-3">
          <Building2 className="h-8 w-8 text-blue-600 flex-shrink-0" />
          <div>
            <div className="text-2xl font-bold text-blue-900">{opdrachtgevers.length}</div>
            <div className="text-sm text-blue-700">{t('belastingdienst.differentEmployers')}</div>
          </div>
        </div>
        <div className="bg-red-50 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="h-8 w-8 text-red-500 flex-shrink-0" />
          <div>
            <div className="text-2xl font-bold text-red-900">{geweigerd.length}</div>
            <div className="text-sm text-red-700">{t('belastingdienst.rejectedAssignments')}</div>
          </div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 flex items-center gap-3">
          <Euro className="h-8 w-8 text-green-600 flex-shrink-0" />
          <div>
            <div className="text-2xl font-bold text-green-900">{afspraken.length}</div>
            <div className="text-sm text-green-700">{t('belastingdienst.rateAgreements')}</div>
          </div>
        </div>
      </div>

      {/* Opdrachtgevers */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Building2 className="h-5 w-5 text-blue-600" />
          {t('belastingdienst.employersWorked')}
        </h3>
        {opdrachtgevers.length === 0 ? (
          <p className="text-sm text-gray-500 italic">{t('belastingdienst.noEmployers')}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thEmployer')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thEmail')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thType')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thStarted')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thStatus')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {opdrachtgevers.map((c) => (
                  <tr key={c.connection_id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{employerName(c)}</td>
                    <td className="px-4 py-3 text-gray-600">{c.employer_email}</td>
                    <td className="px-4 py-3 text-gray-600 capitalize">{c.connection_type || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(c.connected_at)}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.status === 'active' ? 'bg-green-100 text-green-800' :
                        c.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-700'}`}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Geweigerde opdrachten */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <XCircle className="h-5 w-5 text-red-500" />
          {t('belastingdienst.rejectedSection')}
        </h3>
        {geweigerd.length === 0 ? (
          <p className="text-sm text-gray-500 italic">{t('belastingdienst.noRejected')}</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thEmployer')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thEmail')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thRequested')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thRejectedAt')}</th>
                  <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thReason')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {geweigerd.map((c) => (
                  <tr key={c.connection_id}>
                    <td className="px-4 py-3 font-medium text-gray-900">{employerName(c)}</td>
                    <td className="px-4 py-3 text-gray-600">{c.employer_email}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(c.aangevraagd_op)}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(c.terminated_at)}</td>
                    <td className="px-4 py-3 text-gray-500 italic">{c.termination_reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Tariefafspraken */}
      <section>
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FileText className="h-5 w-5 text-green-600" />
          {t('belastingdienst.rateAgreementsSection')}
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          {t('belastingdienst.rateAgreementsNote')}
        </p>
        {afspraken.length === 0 ? (
          <p className="text-sm text-gray-500 italic">{t('belastingdienst.noRateAgreements')}</p>
        ) : (
          <div className="space-y-3">
            {afspraken.map((a) => (
              <div key={a.afspraak_id} className="rounded-lg border border-gray-200 bg-white p-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium text-gray-900">
                      {a.bedrijfsnaam || `${a.employer_first_name} ${a.employer_last_name}`}
                    </div>
                    <div className="text-xs text-gray-500">{a.employer_email}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-green-700">{formatEuro(a.hourly_rate)}{t('belastingdienst.perHour')}</div>
                    <div className="text-xs text-gray-500">{t('belastingdienst.agreedRate')}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    {a.approved_by_professional ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-yellow-500" />}
                    {a.approved_by_professional ? t('belastingdienst.professionalApproved') : t('belastingdienst.professionalPending')}
                  </span>
                  <span className="flex items-center gap-1">
                    {a.approved_by_employer ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500" /> : <Clock className="h-3.5 w-3.5 text-yellow-500" />}
                    {a.approved_by_employer ? t('belastingdienst.employerApproved') : t('belastingdienst.employerPending')}
                  </span>
                  <span>{t('belastingdienst.createdAt')} {formatDate(a.created_at)}</span>
                  {a.approved_at && <span>{t('belastingdienst.signedAt')} {formatDate(a.approved_at)}</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

// ─── Employer export tab ──────────────────────────────────────────────────────

const EmployerExportTab: React.FC = () => {
  const { t } = useTranslation('pages');
  const [downloading, setDownloading] = useState<string | null>(null);

  const { data: employers, isLoading, isError } = useQuery<EmployerSummary[]>({
    queryKey: ['compliance-employers'],
    queryFn: async () => {
      const resp = await api.get('/admin/compliance/employers');
      return resp.data.data;
    },
  });

  const handleDownload = async (employerId: string, employerDisplayName: string) => {
    setDownloading(employerId);
    try {
      const filename = `compliance-inlener-${employerDisplayName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().slice(0, 10)}.pdf`;
      await downloadPdf(`/admin/compliance/employer/${employerId}/pdf`, filename);
    } finally {
      setDownloading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (isError) {
    return <div className="text-center py-20 text-red-600">{t('belastingdienst.loadEmployerError')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <strong>{t('belastingdienst.employerExportTitle')}</strong> — {t('belastingdienst.employerExportDesc')}
      </div>

      {!employers || employers.length === 0 ? (
        <p className="text-sm text-gray-500 italic text-center py-10">{t('belastingdienst.noEmployersFound')}</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thEmployerCol')}</th>
                <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.thEmailCol')}</th>
                <th className="px-4 py-3 text-center font-medium text-gray-500">{t('belastingdienst.thProfessionalsCol')}</th>
                <th className="px-4 py-3 text-right font-medium text-gray-500">{t('belastingdienst.thExportCol')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {employers.map((e) => {
                const display = e.bedrijfsnaam || `${e.first_name} ${e.last_name}`;
                return (
                  <tr key={e.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{display}</div>
                      {e.bedrijfsnaam && (
                        <div className="text-xs text-gray-400">{e.first_name} {e.last_name}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{e.email}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-100 text-blue-800 text-xs font-bold">
                        {e.aantal_professionals}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleDownload(e.id, display)}
                        disabled={downloading === e.id}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" />
                        {downloading === e.id ? t('belastingdienst.generating') : t('belastingdienst.downloadEmployerPdf')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            {t(employers.length !== 1 ? 'belastingdienst.employerCountPlural' : 'belastingdienst.employerCount', { count: employers.length })}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const BelastingdienstPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'zzpers' | 'inleners'>('zzpers');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data: professionals, isLoading, isError } = useQuery<ProfessionalSummary[]>({
    queryKey: ['belastingdienst-professionals'],
    queryFn: async () => {
      const resp = await api.get('/admin/belastingdienst/professionals');
      return resp.data.data;
    },
  });

  if (!user || user.role !== 'admin') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <div className="bg-white rounded-lg border p-6">
          <p className="text-sm text-gray-700">{t('belastingdienst.adminOnly')}</p>
        </div>
      </div>
    );
  }

  if (selectedId) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ProfessionalDetailView professionalId={selectedId} onBack={() => setSelectedId(null)} />
      </div>
    );
  }

  const filtered = (professionals || []).filter((p) => {
    const q = search.toLowerCase();
    return (
      !q ||
      p.first_name.toLowerCase().includes(q) ||
      p.last_name.toLowerCase().includes(q) ||
      p.email.toLowerCase().includes(q) ||
      (p.kvk_nummer || '').includes(q)
    );
  });

  const handleDownloadProfPdf = async (prof: ProfessionalSummary, e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloadingId(prof.id);
    try {
      const filename = `compliance-${prof.last_name.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.pdf`;
      await downloadPdf(`/admin/compliance/professional/${prof.id}/pdf`, filename);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back */}
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('belastingdienst.backToAdmin')}
        </button>
      </div>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('belastingdienst.title')}</h1>
        <p className="mt-1 text-gray-600 text-sm">{t('belastingdienst.subtitle')}</p>
      </div>

      {/* Legend */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="flex items-start gap-3 bg-blue-50 rounded-lg p-4">
          <Building2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-blue-900 text-sm">{t('belastingdienst.legendEmployers')}</div>
            <div className="text-xs text-blue-700 mt-0.5">{t('belastingdienst.legendEmployersDesc')}</div>
          </div>
        </div>
        <div className="flex items-start gap-3 bg-red-50 rounded-lg p-4">
          <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-red-900 text-sm">{t('belastingdienst.legendRejected')}</div>
            <div className="text-xs text-red-700 mt-0.5">{t('belastingdienst.legendRejectedDesc')}</div>
          </div>
        </div>
        <div className="flex items-start gap-3 bg-green-50 rounded-lg p-4">
          <Euro className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-medium text-green-900 text-sm">{t('belastingdienst.legendRates')}</div>
            <div className="text-xs text-green-700 mt-0.5">{t('belastingdienst.legendRatesDesc')}</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          <button
            onClick={() => setActiveTab('zzpers')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'zzpers'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t('belastingdienst.tabZzpers')}
              {professionals && (
                <span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">{professionals.length}</span>
              )}
            </span>
          </button>
          <button
            onClick={() => setActiveTab('inleners')}
            className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'inleners'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {t('belastingdienst.tabEmployers')}
              <span className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full font-semibold">{t('belastingdienst.tabNew')}</span>
            </span>
          </button>
        </nav>
      </div>

      {/* Tab: Inleners */}
      {activeTab === 'inleners' && <EmployerExportTab />}

      {/* Tab: ZZP'ers */}
      {activeTab === 'zzpers' && (
        <>
          {/* Search */}
          <div className="mb-4">
            <input
              type="text"
              placeholder={t('belastingdienst.searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          ) : isError ? (
            <div className="text-center py-20 text-red-600">{t('belastingdienst.loadError')}</div>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.colProfessional')}</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-500">{t('belastingdienst.colType')}</th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500">
                        <span className="flex items-center justify-center gap-1">
                          <Building2 className="h-4 w-4 text-blue-500" />
                          {t('belastingdienst.colEmployers')}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500">
                        <span className="flex items-center justify-center gap-1">
                          <XCircle className="h-4 w-4 text-red-400" />
                          {t('belastingdienst.colRejected')}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500">
                        <span className="flex items-center justify-center gap-1">
                          <Euro className="h-4 w-4 text-green-500" />
                          {t('belastingdienst.colRates')}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-gray-500">
                        <span className="flex items-center justify-center gap-1">
                          <ShieldCheck className="h-4 w-4 text-purple-500" />
                          {t('belastingdienst.colCompliance')}
                        </span>
                      </th>
                      <th className="px-4 py-3 text-right font-medium text-gray-500">{t('belastingdienst.colActions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-10 text-center text-gray-500 italic">
                          {t('belastingdienst.noZzpers')}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((prof) => (
                        <tr key={prof.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{prof.first_name} {prof.last_name}</div>
                            <div className="text-xs text-gray-500">{prof.email}</div>
                            {prof.kvk_nummer && (
                              <div className="text-xs text-gray-400">{t('belastingdienst.kvkLabel')} {prof.kvk_nummer}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-600 capitalize">
                            {prof.professional_type?.replace(/_/g, ' ') || '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                              prof.aantal_opdrachtgevers >= 2 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {prof.aantal_opdrachtgevers}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                              prof.aantal_geweigerd > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {prof.aantal_geweigerd}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {prof.eigen_tarieven.length === 0 ? (
                              <span className="text-gray-400">—</span>
                            ) : (
                              <div className="flex flex-col items-center gap-0.5">
                                {prof.eigen_tarieven.slice(0, 2).map((tarief, i) => (
                                  <span key={i} className="text-xs bg-green-50 text-green-800 px-2 py-0.5 rounded-full">
                                    {formatEuro(tarief)}/u
                                  </span>
                                ))}
                                {prof.eigen_tarieven.length > 2 && (
                                  <span className="text-xs text-gray-400">+{prof.eigen_tarieven.length - 2} meer</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <ScoreBadge score={complianceScore(prof)} />
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={(e) => handleDownloadProfPdf(prof, e)}
                                disabled={downloadingId === prof.id}
                                title={t('belastingdienst.downloadCompliancePdf')}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-60 transition-colors"
                              >
                                <Download className="h-3.5 w-3.5" />
                                {downloadingId === prof.id ? '…' : t('belastingdienst.downloadPdf')}
                              </button>
                              <button
                                onClick={() => setSelectedId(prof.id)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                <Users className="h-3.5 w-3.5" />
                                {t('belastingdienst.dossier')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {filtered.length > 0 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
                  {t(filtered.length !== 1 ? 'belastingdienst.zzperCountPlural' : 'belastingdienst.zzperCount', { count: filtered.length })}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default BelastingdienstPage;
